import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createCampaign } from '../../hooks/useCampaigns';
import { usePatients } from '../../hooks/usePatients';
import { toast } from 'react-hot-toast';
import { getCurrentUserWithClinic } from '../../lib/getCurrentUserWithClinic';
import { ArrowLeft, Mail, MessageSquare, Lock, Clock, Send, Check, Plus, X } from 'lucide-react';

const CampaignBuilderPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const preselected = location.state?.patientIds || [];

  const [campaignName, setCampaignName] = useState('');
  const [campaignType, setCampaignType] = useState('simple');
  const [messages, setMessages] = useState(['']);
  const [selectedPatientIds, setSelectedPatientIds] = useState(preselected);
  const [sendSecure, setSendSecure] = useState(false);
  const [sendMethod, setSendMethod] = useState('now');
  const [scheduledAt, setScheduledAt] = useState('');
  const [channel, setChannel] = useState('sms');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { patients, loading } = usePatients();

  const handleMessageChange = (index, value) => {
    const updated = [...messages];
    updated[index] = value;
    setMessages(updated);
  };

  const addMessage = () => {
    if (messages.length < 5) {
      setMessages([...messages, '']);
    }
  };

  const removeMessage = (index) => {
    if (messages.length > 1) {
      setMessages(messages.filter((_, i) => i !== index));
    }
  };

  const toggleSelect = (id) => {
    setSelectedPatientIds((prev) =>
      prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    setSelectedPatientIds(patients.map(p => p.id));
  };

  const deselectAll = () => {
    setSelectedPatientIds([]);
  };

  const isFormValid = campaignName.trim() !== '' && selectedPatientIds.length > 0 && messages.some(msg => msg.trim() !== '');

  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const { clinicId, user } = await getCurrentUserWithClinic();

      const result = await createCampaign({
        name: campaignName,
        type: campaignType,
        messages,
        channel,
        patientIds: selectedPatientIds,
        created_by: user.id,
        clinic_id: clinicId,
        send_secure: sendSecure,
        start_date: sendMethod === 'scheduled' ? new Date(scheduledAt) : new Date(),
      });

      if (result.error) {
        toast.error('Failed to create campaign');
      } else {
        toast.success(`Campaign "${result.campaign.name}" created for ${result.patientCount} patients.`);
        navigate('/campaigns');
      }
    } catch (err) {
      toast.error('Failed to create campaign');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/campaigns')}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Campaigns
        </button>
        <h1 className="text-2xl font-semibold text-slate-900">Create Campaign</h1>
        <p className="mt-1 text-sm text-slate-500">
          Set up a new outreach campaign for your patients
        </p>
      </div>

      {/* Form */}
      <div className="space-y-6">
        {/* Campaign Name */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <label className="block text-sm font-medium text-slate-900 mb-2">
            Campaign Name
          </label>
          <input
            type="text"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-shadow"
            placeholder="e.g. Follow-up for High Risk Patients"
          />
        </div>

        {/* Campaign Type */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <label className="block text-sm font-medium text-slate-900 mb-3">
            Campaign Type
          </label>
          <div className="flex gap-3">
            <button
              onClick={() => {
                setCampaignType('simple');
                setMessages(['']);
              }}
              className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all border ${
                campaignType === 'simple'
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
            >
              Simple
              <span className="block text-xs mt-0.5 opacity-70">Single message</span>
            </button>
            <button
              onClick={() => {
                setCampaignType('drip');
                if (messages.length === 1) setMessages(['', '', '']);
              }}
              className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all border ${
                campaignType === 'drip'
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
            >
              Drip
              <span className="block text-xs mt-0.5 opacity-70">Multiple messages over time</span>
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <label className="block text-sm font-medium text-slate-900 mb-3">
            Messages
          </label>
          <div className="space-y-4">
            {messages.map((msg, idx) => (
              <div key={idx} className="relative">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500">
                    Message {idx + 1}
                  </span>
                  {messages.length > 1 && (
                    <button
                      onClick={() => removeMessage(idx)}
                      className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <textarea
                  value={msg}
                  onChange={(e) => handleMessageChange(idx, e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-shadow resize-none"
                  rows={3}
                  placeholder={`Enter your message...`}
                />
              </div>
            ))}
            {campaignType === 'drip' && messages.length < 5 && (
              <button
                onClick={addMessage}
                className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add another message
              </button>
            )}
          </div>
        </div>

        {/* Delivery Options */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <label className="block text-sm font-medium text-slate-900 mb-4">
            Delivery Options
          </label>

          {/* Channel */}
          <div className="mb-6">
            <span className="block text-xs font-medium text-slate-500 mb-2">Channel</span>
            <div className="flex gap-3">
              <button
                onClick={() => setChannel('email')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all border ${
                  channel === 'email'
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
              >
                <Mail className="h-4 w-4" />
                Email
              </button>
              <button
                onClick={() => setChannel('sms')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all border ${
                  channel === 'sms'
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
              >
                <MessageSquare className="h-4 w-4" />
                SMS
              </button>
            </div>
          </div>

          {/* Timing */}
          <div className="mb-6">
            <span className="block text-xs font-medium text-slate-500 mb-2">Timing</span>
            <div className="flex gap-3">
              <button
                onClick={() => setSendMethod('now')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all border ${
                  sendMethod === 'now'
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
              >
                <Send className="h-4 w-4" />
                Send Now
              </button>
              <button
                onClick={() => setSendMethod('scheduled')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all border ${
                  sendMethod === 'scheduled'
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
              >
                <Clock className="h-4 w-4" />
                Schedule
              </button>
            </div>
            {sendMethod === 'scheduled' && (
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="mt-3 px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-shadow"
              />
            )}
          </div>

          {/* Secure Message */}
          <button
            onClick={() => setSendSecure(!sendSecure)}
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg border transition-all ${
              sendSecure
                ? 'bg-slate-50 border-slate-300'
                : 'bg-white border-slate-200 hover:border-slate-300'
            }`}
          >
            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
              sendSecure ? 'bg-slate-900 border-slate-900' : 'border-slate-300'
            }`}>
              {sendSecure && <Check className="h-3 w-3 text-white" />}
            </div>
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-700">Send as Secure Message</span>
            </div>
          </button>
        </div>

        {/* Patient Selection */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-900">
                Select Patients
              </label>
              <span className="text-xs text-slate-500">
                {selectedPatientIds.length} of {patients.length} selected
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="text-xs text-slate-600 hover:text-slate-900 transition-colors"
              >
                Select all
              </button>
              <span className="text-slate-300">|</span>
              <button
                onClick={deselectAll}
                className="text-xs text-slate-600 hover:text-slate-900 transition-colors"
              >
                Clear
              </button>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-16 bg-slate-50 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
              {patients.map((patient) => {
                const isSelected = selectedPatientIds.includes(patient.id);
                return (
                  <button
                    key={patient.id}
                    onClick={() => toggleSelect(patient.id)}
                    className={`relative text-left p-3 rounded-lg border transition-all ${
                      isSelected
                        ? 'bg-slate-50 border-slate-900'
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className={`absolute top-2 right-2 w-4 h-4 rounded-full border flex items-center justify-center ${
                      isSelected ? 'bg-slate-900 border-slate-900' : 'border-slate-300'
                    }`}>
                      {isSelected && <Check className="h-2.5 w-2.5 text-white" />}
                    </div>
                    <p className="text-sm font-medium text-slate-900 pr-6 truncate">{patient.name}</p>
                    <p className="text-xs text-slate-500 truncate">{patient.email || patient.phone || 'No contact'}</p>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4">
          <button
            onClick={() => navigate('/campaigns')}
            className="px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${
              isFormValid && !isSubmitting
                ? 'bg-slate-900 text-white hover:bg-slate-800'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Launch Campaign
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignBuilderPage;
